<template>
  <section id="pricing" class="pricing-section section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">GAC Code 超值套餐</h2>
        <p class="section-subtitle">
          🌐 国内稳定镜像，无需梯子 | 🔄 积分回血机制 | 🛠️ 工单重置支持<br>
          相比官网更优惠的价格，享受同样强大的 Claude Code 功能！
        </p>
        <div class="demo-notice">
          💭 说明：此页面仅作展示用途，不提供实际服务
        </div>
      </div>
      
      <!-- 价格对比表格 -->
      <div class="comparison-table">
        <div class="table-header">
          <div class="header-cell">套餐类型</div>
          <div class="header-cell">GAC原价格</div>
          <div class="header-cell">活动限时价格</div>
          <div class="header-cell">初始积分</div>
          <div class="header-cell">每小时回血</div>
          <div class="header-cell">重置支持</div>
        </div>
        
        <div class="table-row" v-for="comparison in priceComparisons" :key="comparison.id">
          <div class="cell plan-name-cell">
            <span class="plan-icon">{{ comparison.icon }}</span>
            {{ comparison.name }}
          </div>
          <div class="cell official-price">{{ comparison.officialPrice }}</div>
          <div class="cell our-price">
            <span class="price-highlight">{{ comparison.ourPrice }}</span>
          </div>
          <div class="cell initial-points">{{ comparison.initialPoints }}</div>
          <div class="cell regen-rate">{{ comparison.regenRate }}</div>
          <div class="cell reset-support">{{ comparison.resetSupport }}</div>
        </div>
      </div>
      
      <!-- GAC 套餐卡片 -->
      <div class="pricing-section-title">
        <h3>GAC Code 超值套餐</h3>
        <p>🚀 国内镜像，积分回血，超值价格！</p>
      </div>
      <div class="pricing-grid gac-plans">
        <div 
          class="pricing-card" 
          :class="{ featured: plan.featured }"
          v-for="plan in pricingPlans" 
          :key="plan.id"
        >
          <div class="plan-header">
            <div class="plan-badge" v-if="plan.savings">省{{ plan.savings }}元</div>
            <h3 class="plan-name">{{ plan.name }}</h3>
            <div class="plan-price">
              <span class="original-price" v-if="plan.originalPrice">{{ plan.originalPrice }}</span>
              <span class="price">{{ plan.price }}</span>
            </div>
            <p class="plan-description">{{ plan.description }}</p>
          </div>
          
          <ul class="feature-list">
            <li v-for="feature in plan.features" :key="feature">
              <span class="checkmark">✓</span>
              {{ feature }}
            </li>
          </ul>
          
          <button 
            class="btn plan-button btn-disabled" 
            disabled
          >
            仅作展示
          </button>
        </div>
      </div>
      
      <!-- 官方套餐对比 -->
      <div class="official-pricing-section">
        <div class="pricing-section-title">
          <h3>🌐 Claude 官方套餐对比</h3>
          <p>查看官方价格，了解 GAC 的价格优势</p>
        </div>
        <div class="official-plans-grid">
          <div class="official-plan-card" v-for="officialPlan in officialPlans" :key="officialPlan.id">
            <div class="plan-icon">
              <span class="icon-bg" :style="{ background: officialPlan.iconColor }">
                <span class="icon-symbol">⚡</span>
              </span>
            </div>
            <div class="plan-info">
              <h4 class="plan-name">{{ officialPlan.name }}</h4>
              <div class="plan-price">
                <span class="price">${{ officialPlan.price }}</span>
                <span class="period">/month</span>
              </div>
              <p class="plan-description">{{ officialPlan.description }}</p>
            </div>
            <div class="plan-features">
              <ul class="feature-list">
                <li v-for="feature in officialPlan.features" :key="feature">
                  <span class="checkmark">✓</span>
                  {{ feature }}
                </li>
              </ul>
            </div>
            <div class="plan-action">
              <a :href="officialPlan.link" target="_blank" class="btn btn-outline">
                {{ officialPlan.buttonText }}
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <div class="usage-notes">
        <h3 class="notes-title">📋 重要说明</h3>
        <div class="notes-grid">
          <div class="note-card">
            <div class="note-icon">🔄</div>
            <h4>积分机制</h4>
            <p>1 积分 = 7000 tokens<br>积分每小时自动回血<br>不限制会话次数</p>
          </div>
          <div class="note-card">
            <div class="note-icon">🛠️</div>
            <h4>重置规则</h4>
            <p>首次重置：客户工单秒通过<br>后续重置：2-3次机会<br>MAX套餐：1-2次重置</p>
          </div>
          <div class="note-card">
            <div class="note-icon">⏰</div>
            <h4>高峰期限制</h4>
            <p>15:00-18:00 高峰时段<br>暂停重置功能<br>其他时间正常重置</p>
          </div>
          <div class="note-card">
            <div class="note-icon">💭</div>
            <h4>展示说明</h4>
            <p>本页面仅用于展示用途<br>不提供实际服务<br>价格信息仅作参考</p>
          </div>
        </div>
      </div>

      <div class="value-proposition">
        <h3 class="value-title">🎯 为什么选择我们的套餐？</h3>
        <div class="value-points">
          <div class="value-point">
            <span class="value-icon">🌐</span>
            <span class="value-text">国内镜像：无需梯子，访问稳定快速</span>
          </div>
          <div class="value-point">
            <span class="value-icon">🔄</span>
            <span class="value-text">积分回血：每小时自动恢复，持续使用</span>
          </div>
          <div class="value-point">
            <span class="value-icon">🛠️</span>
            <span class="value-text">工单重置：首次重置秒通过，高峰期避开</span>
          </div>
          <div class="value-point">
            <span class="value-icon">💰</span>
            <span class="value-text">超值价格：比官网更优惠，性价比更高</span>
          </div>
        </div>
      </div>
    </div>

  </section>
</template>

<script>
export default {
  name: 'PricingSection',
  data() {
    return {
      selectedPlan: null,
      paymentStep: 1,
      verificationCode: '',
      generatedCode: '',
      userInputCode: '',
      priceComparisons: [
        {
          id: 1,
          icon: '⚡',
          name: '周卡',
          officialPrice: '¥99',
          ourPrice: '¥90',
          initialPoints: '5400积分',
          regenRate: '100/小时',
          resetSupport: '首次秒通过'
        },
        {
          id: 2,
          icon: '🚀',
          name: '轻量月卡',
          officialPrice: '¥169',
          ourPrice: '¥160',
          initialPoints: '2700积分',
          regenRate: '50/小时',
          resetSupport: '首次秒通过'
        },
        {
          id: 3,
          icon: '💎',
          name: '月卡',
          officialPrice: '¥299',
          ourPrice: '¥288',
          initialPoints: '5400积分',
          regenRate: '100/小时',
          resetSupport: '首次秒通过'
        },
        {
          id: 4,
          icon: '👑',
          name: '月卡MAX',
          officialPrice: '¥599',
          ourPrice: '¥588',
          initialPoints: '12000积分',
          regenRate: '300/小时',
          resetSupport: '更高优先级'
        }
      ],
      officialPlans: [
        {
          id: 'free',
          name: 'Free',
          price: '0',
          description: 'Basic Claude access with limited usage (about 20 searches per day)',
          iconColor: '#6b7280',
          features: [
            'Access to Claude on web, iOS, and Android',
            'Ask about images and documents',
            'Analyze, describe, summarize, and transcribe',
            'Access to Claude\'s latest models',
            'Basic chat functionality'
          ],
          buttonText: 'Get Started',
          link: 'https://claude.ai/'
        },
        {
          id: 'pro',
          name: 'Pro',
          price: '20',
          description: 'Enhanced usage with 5x more capacity than Free plan',
          iconColor: '#3b82f6',
          features: [
            '5x more usage than Free plan',
            'Around 45 messages every 5 hours',
            'Access to Claude 4 Sonnet and Opus',
            'Claude Code terminal access',
            'Priority access to new features',
            'Extended thinking mode'
          ],
          buttonText: 'Get Started',
          link: 'https://claude.ai/'
        },
        {
          id: 'max-expanded',
          name: 'Max - Expanded Usage',
          price: '100',
          description: '5x higher rate limits than Claude Pro for frequent users',
          iconColor: '#c53030',
          features: [
            '5x more usage than Pro plan',
            'At least 225 messages every 5 hours',
            'Access to Claude 4 Sonnet and Opus',
            'Priority access to new features',
            'Claude Code terminal access',
            'Extended thinking mode',
            'Advanced research capabilities',
            'Integration capabilities'
          ],
          buttonText: 'Get Started',
          link: 'https://claude.ai/'
        },
        {
          id: 'max-flexibility',
          name: 'Max - Maximum Flexibility',
          price: '200',
          description: '20x higher rate limits for daily users who collaborate with Claude',
          iconColor: '#7c3aed',
          features: [
            '20x more usage than Pro plan',
            'At least 900 messages every 5 hours',
            'Maximum usage capacity',
            'Priority access to all features',
            'Claude Code terminal access',
            'Extended thinking mode',
            'Advanced research capabilities',
            'Integration capabilities',
            'Premium support',
            'Session cap: 50 sessions/month'
          ],
          buttonText: 'Get Started',
          link: 'https://claude.ai/'
        }
      ],
      pricingPlans: [
        {
          id: 1,
          name: '周卡套餐',
          price: '¥90',
          originalPrice: '¥99',
          description: '短期体验，高性价比选择',
          savings: '9',
          features: [
            '🎯 5400 初始积分（约 37.8M tokens）',
            '🔄 每小时回血 100 积分',
            '⏰ 7天有效期',
            '🛠️ 首次重置秒通过',
            '🌐 国内镜像无需梯子',
            '💬 不限制会话次数'
          ],
          buttonText: '立即购买',
          featured: false
        },
        {
          id: 2,
          name: '轻量月卡',
          price: '¥160',
          originalPrice: '¥169',
          description: '个人开发者首选',
          savings: '9',
          features: [
            '🎯 2700 初始积分（约 18.9M tokens）',
            '🔄 每小时回血 50 积分',
            '⏰ 30天有效期',
            '🛠️ 首次重置秒通过',
            '🌐 国内镜像无需梯子',
            '💬 不限制会话次数'
          ],
          buttonText: '立即购买',
          featured: false
        },
        {
          id: 3,
          name: '月卡',
          price: '¥288',
          originalPrice: '¥299',
          description: '专业开发者推荐',
          savings: '11',
          features: [
            '🎯 5400 初始积分（约 37.8M tokens）',
            '🔄 每小时回血 100 积分',
            '⏰ 30天有效期',
            '🛠️ 首次重置秒通过',
            '🌐 国内镜像无需梯子',
            '💬 不限制会话次数'
          ],
          buttonText: '立即购买',
          featured: true
        },
        {
          id: 4,
          name: '月卡MAX',
          price: '¥588',
          originalPrice: '¥599',
          description: '团队协作超值选择',
          savings: '11',
          features: [
            '🎯 12000 初始积分（约 84M tokens）',
            '🔄 每小时回血 300 积分',
            '⏰ 30天有效期',
            '🛠️ 1-2次重置机会',
            '🌐 国内镜像无需梯子',
            '💬 不限制会话次数'
          ],
          buttonText: '立即购买',
          featured: false
        }
      ]
    }
  },
  methods: {}
}
</script>

<style scoped>
.pricing-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.25rem;
  color: #64748b;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  margin-bottom: 20px;
}

.demo-notice {
  background: linear-gradient(135deg, #fef3c7, #fcd34d);
  border: 1px solid #f59e0b;
  border-radius: 12px;
  padding: 16px 24px;
  margin: 0 auto;
  max-width: 500px;
  text-align: center;
  color: #92400e;
  font-weight: 600;
}

/* 价格对比表格样式 */
.comparison-table {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  margin-bottom: 80px;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr 1.5fr 1.5fr 1fr;
  background: linear-gradient(135deg, #1e293b, #334155);
  color: white;
  padding: 0;
}

.header-cell {
  padding: 20px 16px;
  font-weight: 600;
  text-align: center;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}

.header-cell:last-child {
  border-right: none;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr 1.5fr 1.5fr 1fr;
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.3s ease;
}

.table-row:hover {
  background: #f8fafc;
}

.cell {
  padding: 20px 16px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.plan-name-cell {
  justify-content: flex-start;
  font-weight: 600;
  color: #1e293b;
}

.plan-icon {
  margin-right: 8px;
  font-size: 1.2rem;
}

.official-price {
  color: #ef4444;
  font-weight: 600;
  text-decoration: line-through;
}

.our-price .price-highlight {
  color: #10b981;
  font-weight: 700;
  font-size: 1.1rem;
}

.savings .savings-amount {
  background: #dcfce7;
  color: #15803d;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 600;
}

.requests {
  color: #3b82f6;
  font-weight: 600;
}

.duration {
  color: #64748b;
}

/* 重要说明样式 */
.usage-notes {
  background: linear-gradient(135deg, #fef3c7, #fcd34d);
  border-radius: 16px;
  padding: 40px;
  margin-bottom: 60px;
}

.notes-title {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 32px;
}

.notes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.note-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid #fbbf24;
}

.note-icon {
  font-size: 2rem;
  margin-bottom: 12px;
}

.note-card h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 12px;
}

.note-card p {
  color: #64748b;
  line-height: 1.6;
  font-size: 0.875rem;
}

/* 价值主张样式 */
.value-proposition {
  background: linear-gradient(135deg, #1e293b, #334155);
  color: white;
  padding: 40px;
  border-radius: 16px;
  text-align: center;
}

.value-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 32px;
}

.value-points {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.value-point {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.value-icon {
  font-size: 1.5rem;
}

.value-text {
  font-size: 1rem;
  line-height: 1.5;
}

.pricing-section-title {
  text-align: center;
  margin-bottom: 48px;
}

.pricing-section-title h3 {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
}

.pricing-section-title p {
  font-size: 1.125rem;
  color: #64748b;
  margin: 0;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 80px;
}

.gac-plans {
  margin-bottom: 120px;
}

/* 官方套餐样式 */
.official-pricing-section {
  margin-top: 80px;
  padding-top: 80px;
  border-top: 2px solid #e2e8f0;
}

.official-plans-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.official-plan-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.official-plan-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
  transform: translateY(-4px);
}

.plan-icon {
  margin-bottom: 16px;
}

.icon-bg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
}

.icon-symbol {
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
}

.plan-info {
  margin-bottom: 20px;
}

.official-plan-card .plan-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.official-plan-card .plan-price {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 8px;
}

.official-plan-card .price {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
}

.official-plan-card .period {
  color: #64748b;
  font-size: 0.875rem;
}

.official-plan-card .plan-description {
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

.plan-features {
  flex: 1;
  margin-bottom: 20px;
}

.official-plan-card .feature-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.official-plan-card .feature-list li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.4;
}

.official-plan-card .checkmark {
  color: #10b981;
  font-weight: 600;
  flex-shrink: 0;
  margin-top: 2px;
}

.btn-outline {
  background: transparent;
  color: #3b82f6;
  border: 1px solid #3b82f6;
  padding: 10px 20px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  text-align: center;
  transition: all 0.2s ease;
  display: block;
  width: 100%;
}

.btn-outline:hover {
  background: #3b82f6;
  color: white;
  transform: translateY(-1px);
}

.pricing-card {
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
  position: relative;
}

.pricing-card.featured {
  border-color: #3b82f6;
  transform: scale(1.05);
}

.pricing-card.featured::before {
  content: '最受欢迎';
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  padding: 6px 20px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
}

.plan-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.plan-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
}

.original-price {
  color: #64748b;
  text-decoration: line-through;
  font-size: 1.2rem;
}

.price {
  font-size: 3rem;
  font-weight: 700;
  color: #1e293b;
}

.pricing-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.pricing-card.featured:hover {
  transform: translateY(-8px) scale(1.05);
}

.plan-header {
  text-align: center;
  margin-bottom: 32px;
}

.plan-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.plan-price {
  margin-bottom: 16px;
}

.price {
  font-size: 3rem;
  font-weight: 700;
  color: #1e293b;
}

.period {
  font-size: 1rem;
  color: #64748b;
  margin-left: 4px;
}

.plan-description {
  color: #64748b;
  font-size: 1rem;
}

.feature-list {
  list-style: none;
  margin-bottom: 32px;
}

.feature-list li {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  color: #64748b;
}

.checkmark {
  color: #10b981;
  font-weight: bold;
  margin-right: 12px;
  font-size: 1.2rem;
}

.plan-button {
  width: 100%;
  padding: 16px;
  font-size: 1rem;
}

.btn-disabled {
  background: #e2e8f0 !important;
  color: #94a3b8 !important;
  cursor: not-allowed;
  border: 1px solid #e2e8f0 !important;
}

.btn-disabled:hover {
  background: #e2e8f0 !important;
  color: #94a3b8 !important;
  transform: none !important;
  box-shadow: none !important;
}

.enterprise-section {
  text-align: center;
  padding: 40px;
  background: linear-gradient(135deg, #1e293b, #334155);
  border-radius: 16px;
  color: white;
}

.enterprise-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 16px;
}

.enterprise-description {
  font-size: 1rem;
  line-height: 1.6;
  opacity: 0.9;
}

.enterprise-link {
  color: #60a5fa;
  text-decoration: none;
  font-weight: 600;
}

.enterprise-link:hover {
  color: #3b82f6;
  text-decoration: underline;
}

/* 支付模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 540px;
  width: 92%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background: #f1f5f9;
}

.modal-body {
  padding: 32px;
}

.payment-step h4 {
  text-align: center;
  font-size: 1.25rem;
  margin-bottom: 24px;
  color: #1e293b;
}

.plan-summary {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  margin-bottom: 24px;
}

.plan-summary h5 {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1e293b;
}

.price-display {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
}

.current-price {
  font-size: 2rem;
  font-weight: 700;
  color: #10b981;
}

.price-display .original-price {
  font-size: 1rem;
  color: #64748b;
  text-decoration: line-through;
}

.qr-placeholder {
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border: 2px dashed #3b82f6;
  border-radius: 12px;
  padding: 30px 20px;
  text-align: center;
  margin-bottom: 24px;
}

.payment-qr-image {
  max-width: 280px;
  max-height: 280px;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.qr-icon {
  font-size: 3rem;
  margin-bottom: 12px;
}

.payment-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: #10b981;
  margin-top: 12px;
}

.verification-code {
  margin-bottom: 24px;
}

.verification-code label {
  display: block;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.code-display {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  margin-bottom: 12px;
}

.generated-code {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  font-family: 'SF Mono', Monaco, monospace;
  letter-spacing: 8px;
  display: block;
  margin-bottom: 8px;
}

.code-hint {
  font-size: 0.875rem;
  color: #64748b;
}

.verification-code input {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1.125rem;
  text-align: center;
  font-family: 'SF Mono', Monaco, monospace;
  letter-spacing: 4px;
  transition: border-color 0.2s;
}

.verification-code input:focus {
  outline: none;
  border-color: #3b82f6;
}

.payment-confirm-btn {
  width: 100%;
  padding: 16px;
  font-size: 1rem;
}

.payment-confirm-btn:disabled {
  background: #e2e8f0 !important;
  color: #94a3b8 !important;
  cursor: not-allowed;
}

.success-content {
  text-align: center;
}

.success-icon {
  font-size: 4rem;
  margin-bottom: 16px;
}

.wechat-qr {
  margin: 24px 0;
}

.contact-steps {
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0;
  text-align: left;
}

.contact-steps h5 {
  color: #1e293b;
  margin-bottom: 16px;
}

.contact-steps ol {
  color: #64748b;
  line-height: 1.6;
}

.contact-steps li {
  margin-bottom: 8px;
}

.customer-service {
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
}

.customer-service a {
  color: #0369a1;
  text-decoration: none;
}

.customer-service a:hover {
  text-decoration: underline;
}

@media (max-width: 1200px) {
  .pricing-grid,
  .official-plans-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .pricing-grid,
  .official-plans-grid {
    grid-template-columns: 1fr;
  }
  
  .pricing-section-title h3 {
    font-size: 1.75rem;
  }
  
  .pricing-card.featured {
    transform: none;
  }
  
  .pricing-card.featured:hover {
    transform: translateY(-8px);
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .pricing-card {
    padding: 32px 24px;
  }

  .modal-content {
    width: 96%;
    max-width: 420px;
    max-height: 92vh;
  }

  .modal-header,
  .modal-body {
    padding: 20px;
  }

  .payment-qr-image {
    max-width: 240px;
    max-height: 240px;
  }

  .generated-code {
    font-size: 1.75rem;
    letter-spacing: 6px;
  }

  .table-header,
  .table-row {
    grid-template-columns: 1fr;
  }

  .comparison-table {
    overflow-x: auto;
  }
  
  .official-pricing-section {
    margin-top: 60px;
    padding-top: 60px;
  }
}

</style>